import { forwardRef, useMemo, useRef } from 'react';
import { Input, Popover } from '~/components/granular';
import { useFieldSizingContent } from '~/hooks/useFieldSizingContent';

import type { PopoverInputProps } from './PopoverInput.props';

import './PopoverInput.css';
import { classNames } from '~/utils';

export const PopoverInput = forwardRef<HTMLDivElement, PopoverInputProps>(
  (
    {
      appearance,
      children,
      className,
      clearable,
      hasError,
      height,
      id,
      isDisabled,
      isOpen,
      leftIcon,
      name,
      placeholder,
      value,
      type,
      readOnlyInput,
      inputMax,
      inputMin,
      inputClassName,
      align = 'start',
      customPrefix,
      appendLeft,
      showSelectedState,
      onClear,
      onOpenAutoFocus,
      onTriggerClick,
      onPointerDownOutside,
      onInputPaste,
      onInputBlur,
      onInputChange,
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useFieldSizingContent(inputRef, ['body-200-light', 'pl-6', 'pr-7']);

    function handleOnClick(e: React.MouseEvent<HTMLInputElement>) {
      // Prevent native date picker from opening when clicking on the input
      if (type === 'date') {
        e.preventDefault();
        onTriggerClick?.(!isOpen);
      }
    }

    const inputValue = useMemo(() => {
      if (customPrefix) {
        return value ? `${customPrefix} ${value}` : customPrefix;
      }

      return value;
    }, [value, customPrefix]);

    return (
      <Popover
        align={align}
        disabled={isDisabled}
        onOpenAutoFocus={onOpenAutoFocus}
        onPointerDownOutside={onPointerDownOutside}
        onTriggerClick={onTriggerClick}
        open={isOpen}
        ref={forwardedRef}
        trigger={
          <div className={inputClassName}>
            <Input
              appearance={appearance}
              appendLeft={appendLeft}
              autoComplete="off"
              className={classNames('no-calendar field-sizing-content min-w-full', className)}
              clearable={clearable}
              disabled={isDisabled}
              hasError={hasError}
              height={height}
              id={id}
              leftIcon={leftIcon}
              max={inputMax}
              min={inputMin}
              name={name}
              onBlur={onInputBlur}
              onChange={onInputChange}
              onClear={(e) => {
                // Stops the popover trigger from opening the popover on the same click.
                e.stopPropagation();
                onClear?.();
              }}
              onClick={handleOnClick}
              onPaste={onInputPaste}
              placeholder={placeholder}
              readOnly={readOnlyInput}
              ref={inputRef}
              rightIcon="ArrowDownSIcon"
              showSelectedState={showSelectedState}
              type={type}
              value={inputValue}
            />
          </div>
        }
      >
        {children}
      </Popover>
    );
  },
);
